import { GetString } from 'fluent-react';
import sortBy from 'lodash/sortBy';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  CountryProfile,
  CountryYearRange,
  GlobalDatum,
  ProductClass,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { earliestHSYear, latestYear } from '../../Utils';
import {
  WidgetsContainer,
} from '../landing/Grid';
import { adjustRankValueDirection } from '../Utils';
import { defaultECIProductClass } from '../Utils';
import Widget, {
  Status,
  Type,
} from './Widget';

//#region Styling
const Root = styled(WidgetsContainer)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
//#endregion

const query = gql`
  query LandingPageWidgets($id: ID!, $productClass: ProductClass!, $minYear: Int!, $maxYear: Int!) {
    countryProfile(location: $id) {
      latestGdpPerCapita {
        quantity
        year
      }
      latestGdpPerCapitaRank {
        quantity
        year
      }
      growthProjection
      growthProjectionRank
    }
    countryYearRange(location: $id, minYear: $minYear, maxYear: $maxYear, eciProductClass: $productClass) {
      gdpPerCapita {
        quantity
        year
      }
      eciRank {
        quantity
        year
      }
    }
    globalDatum(productClass: $productClass) {
      latestEciRankTotal
      latestExporterRankTotal
      latestGdpRankTotal
    }

  }
`;

interface SuccessResponse {
  countryProfile: null | {
    latestGdpPerCapita: CountryProfile['latestGdpPerCapita'],
    latestGdpPerCapitaRank: CountryProfile['latestGdpPerCapitaRank'],
    growthProjection: CountryProfile['growthProjection'],
    growthProjectionRank: CountryProfile['growthProjectionRank'],
  };
  countryYearRange: null | {
    gdpPerCapita: CountryYearRange['gdpPerCapita'],
    eciRank: CountryYearRange['eciRank'],
  };
  globalDatum: null | {
    latestEciRankTotal: GlobalDatum['latestEciRankTotal'];
    latestExporterRankTotal: GlobalDatum['latestExporterRankTotal'];
    latestGdpRankTotal: GlobalDatum['latestGdpRankTotal'];
  };
}

interface Variables {
  id: string;
  productClass: ProductClass;
  minYear: number;
  maxYear: number;
}

interface Props {
  countryId: string;
  getFluentString: GetString;
}

const LandingWidgets = (props: Props) => {
  const {countryId, getFluentString} = props;

  const elRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  useEffect(() => {
    const {current: el} = elRef;
    if (el !== null) {
      const size = el.getBoundingClientRect();
      setWidth(Math.floor(size.width * 0.8));
    }
  }, []);

  const variables: Variables = {
    id: countryId,
    productClass: defaultECIProductClass,
    minYear: earliestHSYear,
    maxYear: latestYear,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables: variables, errorPolicy: 'all'});

  let out: React.ReactElement<any> | null;


  if (loading === true) {
    out = null;
  } else if (error !== undefined && data === undefined) {
    out = null;
    console.error(error);
  } else if (data !== undefined && width !== undefined) {

    const {countryProfile, countryYearRange, globalDatum} = data;
    if (countryProfile === null || countryYearRange === null || globalDatum === null) {
      out = null;
    } else {
      const {
        growthProjection,
        growthProjectionRank,
        latestGdpPerCapita,
        latestGdpPerCapitaRank,
      } = countryProfile;
      const {
        // gdpPerCapita: gdpPerCapitaUntransformedTimeSeries,
        gdpPerCapita: gdpPerCapitaUnfilteredTimeSeries,
        // eciRank: eciRankUntransformedTimeSeries,
        eciRank: eciRankUnfilteredTimeSeries,
      } = countryYearRange;
      const {
        latestGdpRankTotal,
        latestExporterRankTotal, latestEciRankTotal,
      } = globalDatum;

      
      // Removing null
      const eciRankUntransformedTimeSeries = eciRankUnfilteredTimeSeries ? eciRankUnfilteredTimeSeries.filter(
        (res) => res !== null
      ) : [];

      const gdpPerCapitaUntransformedTimeSeries = gdpPerCapitaUnfilteredTimeSeries ? gdpPerCapitaUnfilteredTimeSeries.filter(
        (res) => res !== null
      ) : [];

      const latestEciRankSeries = eciRankUntransformedTimeSeries
        ? eciRankUntransformedTimeSeries.find(d => d.year === latestYear) : null;
      const latestEciRank = latestEciRankSeries ? latestEciRankSeries.quantity : null;
      if (latestEciRank === null || latestGdpPerCapita === null ||
        latestGdpPerCapitaRank === null ||
        growthProjection === null || growthProjectionRank === null) {
        out = null;
      } else {
        const gdpPerCapitaPPPTimeSeriesFiltered = gdpPerCapitaUntransformedTimeSeries
          ? gdpPerCapitaUntransformedTimeSeries.filter(
            ({year}) => year >= earliestHSYear && year <= latestYear,
          ) : [];
        const gdpPerCapitaPPPTimeSeriesSorted = sortBy(gdpPerCapitaPPPTimeSeriesFiltered, ['year']);
        const gdpPerCapitaPPPTimeSeries = gdpPerCapitaPPPTimeSeriesSorted.map(
          ({year, quantity}) => ({year, value: quantity}),
        );
        const {
          quantity: gdpPerCapita, year: gdpPerCapitaYear,
        } = latestGdpPerCapita;


        const eciRankTimeSeriesFiltered = eciRankUntransformedTimeSeries ? eciRankUntransformedTimeSeries.filter(
          ({year}) => year >= earliestHSYear && year <= latestYear,
          // (res) => res !== null && res.year >= earliestHSYear && res.year <= latestYear
        ) : [];
        const eciRankTimeSeriesSorted = sortBy(eciRankTimeSeriesFiltered, ['year']);
        // eci rank value should be negative to put the smallest nmber at the top of the graph
        // i.e. the top position (#1) should be at the top, not the bottom
        const eciRankTimeSeries = eciRankTimeSeriesSorted.map(
          ({year, quantity}) => ({year, value: adjustRankValueDirection(quantity)}),
        );
        out = (
          <>
            <Widget
              status={Status.Success}
              type={Type.GDPPC}
              getFluentString={getFluentString}
              size={{width, height: 40}}
              year={gdpPerCapitaYear}
              rank={latestGdpPerCapitaRank.quantity}
              rankTotalCount={latestGdpRankTotal}
              gdpPerCapita={gdpPerCapita}
              timeSeries={gdpPerCapitaPPPTimeSeries}
            />
            <Widget
              status={Status.Success}
              type={Type.ECIRank}
              getFluentString={getFluentString}
              size={{width, height: 40}}
              rank={latestEciRank}
              rankTotalCount={latestEciRankTotal}
              timeSeries={eciRankTimeSeries}
            />
            <Widget
              status={Status.Success}
              type={Type.GrowthProjection}
              getFluentString={getFluentString}
              size={{width, height: 40}}
              growth={growthProjection}
              rank={growthProjectionRank}
              rankTotalCount={latestExporterRankTotal}
            />
          </>
        );

      }
    }
  } else {
    out = null;
  }


  return (
    <Root ref={elRef}>
      {out}
    </Root>
  );
};

export default LandingWidgets;
